import React from 'react';

/* Pieces shared by the orders list (overview cards) and the order details
   page, so the two stay visually identical and only differ in depth. */

/* Neutral fallback shown when a product has no image or it fails to load. */
export const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
      <rect width="46" height="46" rx="10" fill="#f3ede1"/>
      <text x="23" y="30" font-size="22" text-anchor="middle" fill="#c2410c">🛍</text>
    </svg>`
  );

/* The forward delivery flow. Each order status maps to a point on this rail. */
export const ORDER_FLOW = [
  { key: 'Confirmed', label: 'Confirmed', icon: '🧾' },
  { key: 'Processing', label: 'Processing', icon: '📝' },
  { key: 'Shipped', label: 'Shipped', icon: '📦' },
  { key: 'Out For Delivery', label: 'Out for Delivery', icon: '🚚' },
  { key: 'Delivered', label: 'Delivered', icon: '✅' }
];

/* Everything a card needs to theme itself for a given status, in one place.
   `accent` drives the strip, glow, ring and pill via a CSS variable. */
export const STATUS_META = {
  Confirmed: { accent: '#7c3aed', soft: '#f5f3ff', icon: '🧾' },
  Processing: { accent: '#f59e0b', soft: '#fffbeb', icon: '📝' },
  Shipped: { accent: '#3b82f6', soft: '#eff6ff', icon: '📦' },
  'Out For Delivery': { accent: '#06b6d4', soft: '#ecfeff', icon: '🚚' },
  Delivered: { accent: '#22c55e', soft: '#f0fdf4', icon: '✅' },
  Cancelled: { accent: '#ef4444', soft: '#fef2f2', icon: '✖' }
};

export const metaFor = (status) =>
  STATUS_META[status] || { accent: '#9ca3af', soft: '#f8fafc', icon: '•' };

export const IN_TRANSIT = [
  'Confirmed',
  'Processing',
  'Shipped',
  'Out For Delivery'
];

/* An order can only be pulled back before it leaves for delivery. */
export const CANCELLABLE_STATUSES = ['Confirmed', 'Processing', 'Shipped'];

export const canCancel = (status) => CANCELLABLE_STATUSES.includes(status);

/* Five stages now, so each one is worth a fifth of the rail. */
export const getProgress = (status) => {
  switch (status) {
    case 'Confirmed':
      return 20;

    case 'Processing':
      return 40;

    case 'Shipped':
      return 60;

    case 'Out For Delivery':
      return 80;

    case 'Delivered':
      return 100;

    default:
      return 0;
  }
};

/* Days remaining until delivery, based on where the order is in the flow. */
export const getDaysToDeliver = (status) => {
  switch (status) {
    case 'Confirmed':
      return 6;

    case 'Processing':
      return 5;

    case 'Shipped':
      return 3;

    case 'Out For Delivery':
      return 1;

    default:
      return 5;
  }
};

/* Expected delivery date = today + remaining days for the current status. */
export const getExpectedDate = (status) => {
  const base = new Date();
  base.setDate(base.getDate() + getDaysToDeliver(status));
  return base.toLocaleDateString();
};

export const countUnits = (order) =>
  (order?.orderItems || []).reduce((n, item) => n + (item.quantity || 1), 0);

export const formatDate = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

/* Horizontal status timeline. */
export function OrderTimeline({ status }) {
  const current = ORDER_FLOW.findIndex((s) => s.key === status);

  return (
    <div className="order-timeline">
      {ORDER_FLOW.map((stage, i) => {
        const done = i <= current;
        const active = i === current;

        return (
          <React.Fragment key={stage.key}>
            {i > 0 && (
              <span className={`ot-line ${i <= current ? 'is-done' : ''}`} />
            )}
            <div
              className={`ot-node ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}
            >
              <span className="ot-icon">{stage.icon}</span>
              <span className="ot-label">{stage.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* CSS both pages inject — page background, card shell, status header,
   timeline, fact tiles, product rows and the progress rail. */
export const orderCommonStyles = `
  .orders-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(28px, 5vw, 50px) 0;
  }

  /* ===== Order card shell ===== */
  .order-card {
    position: relative;
    border-radius: 22px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.07);
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease;
  }
  .order-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--o-accent), color-mix(in srgb, var(--o-accent) 45%, #fff));
    z-index: 2;
  }
  /* Light sweep on hover — subtle, runs once per hover */
  .order-card-sheen {
    position: absolute;
    top: 0; left: -60%;
    width: 45%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    z-index: 3;
    transition: left 0.75s ease;
  }
  .order-card:hover .order-card-sheen { left: 130%; }

  .order-head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    padding: 16px 22px;
    background: linear-gradient(135deg, var(--o-soft), #fff 70%);
    border-bottom: 1px solid #f4ece1;
  }
  .order-head-id {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }
  .order-avatar {
    width: 46px; height: 46px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    border-radius: 15px;
    color: #fff;
    background: linear-gradient(135deg, var(--o-accent), color-mix(in srgb, var(--o-accent) 55%, #0f172a));
    box-shadow: 0 8px 18px color-mix(in srgb, var(--o-accent) 40%, transparent);
  }
  .order-id-text { min-width: 0; }
  .order-id {
    font-weight: 800;
    letter-spacing: -0.3px;
    color: #0f172a;
  }
  .order-sub {
    font-size: 0.8rem;
    color: #94a3b8;
    font-weight: 500;
    margin-top: 2px;
  }
  .order-head-meta {
    display: flex;
    align-items: center;
    gap: 18px;
  }
  .order-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 700;
    padding: 7px 15px;
    border-radius: 30px;
    color: color-mix(in srgb, var(--o-accent) 72%, #0f172a);
    background: color-mix(in srgb, var(--o-accent) 14%, #fff);
    border: 1px solid color-mix(in srgb, var(--o-accent) 32%, #fff);
    white-space: nowrap;
  }
  .order-status-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--o-accent);
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--o-accent) 60%, transparent);
    animation: orderPing 2s ease-out infinite;
  }
  .order-total { text-align: right; line-height: 1.1; }
  .order-total-label {
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .order-total-value {
    font-size: 1.4rem;
    font-weight: 800;
    background: linear-gradient(135deg, #0f172a, #c2410c);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  /* Consistent column labels for alignment */
  .order-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #a1a1aa;
    margin-bottom: 6px;
  }

  /* Key-fact tiles */
  .order-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 12px;
  }
  .order-info {
    background: #fbfaf8;
    border: 1px solid #f1e4d0;
    border-radius: 14px;
    padding: 12px 14px;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .order-info:hover {
    border-color: color-mix(in srgb, var(--o-accent) 45%, #f1e4d0);
    background: #fff;
    transform: translateY(-2px);
  }
  .order-info-value {
    font-weight: 700;
    color: #1f2937;
    font-size: 0.92rem;
    line-height: 1.3;
  }
  .order-pay-id {
    display: block;
    font-size: 0.68rem;
    word-break: break-all;
  }

  /* ===== Status timeline ===== */
  .order-timeline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 4px 0 2px;
  }
  .ot-node {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    text-align: center;
    width: 84px;
  }
  .ot-icon {
    width: 44px; height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    border-radius: 50%;
    background: #f1f5f9;
    border: 2px solid #e5e7eb;
    filter: grayscale(1);
    opacity: 0.6;
    transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .ot-node.is-done .ot-icon {
    background: linear-gradient(135deg, #c2410c, #ea580c);
    border-color: transparent;
    filter: none;
    opacity: 1;
    box-shadow: 0 6px 14px rgba(234,88,12,0.32);
  }
  .ot-node.is-active .ot-icon {
    transform: scale(1.15);
    animation: otPulse 1.8s ease-in-out infinite;
  }
  .ot-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: #94a3b8;
    line-height: 1.2;
  }
  .ot-node.is-done .ot-label { color: #7c2d12; }
  .ot-line {
    flex: 1 1 auto;
    height: 4px;
    border-radius: 4px;
    background: #eef0f3;
    margin: 22px 4px 0;
    overflow: hidden;
    transition: background 0.3s ease;
  }
  .ot-line.is-done {
    background: linear-gradient(90deg, #c2410c, #ea580c);
  }
  @media (max-width: 575.98px) {
    .order-timeline { overflow-x: auto; gap: 4px; }
    .ot-node { width: 70px; }
    .ot-label { font-size: 0.65rem; }
  }

  .order-cancel-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #fef2f2, #fff);
    border: 1px solid #fecaca;
    color: #b91c1c;
    border-radius: 14px;
    padding: 14px 16px;
  }
  .order-cancel-icon {
    width: 34px; height: 34px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #fee2e2;
    font-weight: 800;
  }

  /* ===== Products ===== */
  .order-products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 10px;
  }
  .order-product {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #f1e4d0;
    border-radius: 14px;
    padding: 8px 12px 8px 8px;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  }
  .order-product:hover {
    border-color: rgba(234, 88, 12, 0.55);
    box-shadow: 0 8px 20px rgba(194, 65, 12, 0.16);
    transform: translateY(-3px);
    color: inherit;
  }
  .order-product:hover .order-product-name { color: #c2410c; }
  .order-product:hover .order-product-img { transform: scale(1.06); }
  .order-product-chevron {
    color: #d1a06a;
    font-size: 1.3rem;
    font-weight: 700;
    flex-shrink: 0;
    transition: transform 0.2s ease, color 0.2s ease;
  }
  .order-product:hover .order-product-chevron {
    color: #ea580c;
    transform: translateX(3px);
  }
  .order-product-img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 12px;
    flex-shrink: 0;
    background: #faf5ff;
    transition: transform 0.25s ease;
  }
  .order-product-info {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
    flex: 1 1 auto;
  }
  .order-product-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s ease;
  }
  .order-product-qty {
    font-size: 0.76rem;
    color: #9ca3af;
  }
  .order-product-price {
    font-weight: 700;
    font-size: 0.9rem;
    color: #16a34a;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ===== Progress rail ===== */
  .order-progress {
    height: 8px;
    border-radius: 8px;
    background: #f1f5f9;
    overflow: hidden;
  }
  .order-progress-fill {
    display: block;
    height: 100%;
    border-radius: 8px;
    background: linear-gradient(90deg, var(--o-accent), color-mix(in srgb, var(--o-accent) 50%, #fbbf24));
    box-shadow: 0 0 12px color-mix(in srgb, var(--o-accent) 55%, transparent);
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .order-progress-label {
    display: block;
    margin-top: 6px;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 600;
  }

  .order-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }
  .order-actions .btn {
    border-radius: 30px;
    font-size: 0.82rem;
    padding: 7px 18px;
    font-weight: 600;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .order-actions .btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(15,23,42,0.14);
  }

  /* Order summary block inside the cancel modal */
  .cancel-order-summary {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 12px;
    padding: 12px 14px;
  }

  @keyframes otPulse {
    0%, 100% { box-shadow: 0 6px 14px rgba(234,88,12,0.3); }
    50% { box-shadow: 0 6px 22px rgba(234,88,12,0.6); }
  }
  @keyframes orderPing {
    0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--o-accent) 60%, transparent); }
    70% { box-shadow: 0 0 0 7px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
  }

  /* Loading skeletons */
  .orders-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: ordersShimmer 1.4s ease infinite;
    border-radius: 22px;
  }
  @keyframes ordersShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ot-node.is-active .ot-icon,
    .order-status-dot,
    .orders-skel { animation: none; }
    .order-card:hover { transform: none; }
    .order-card-sheen { display: none; }
  }

  @media (max-width: 767.98px) {
    .order-head { padding: 16px 16px; }
    .order-head-meta { width: 100%; justify-content: space-between; }
  }
`;
